import { DateOnly } from '@if/core/date'
import { ESourceSystem } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Ban } from '../../types'

// Query Keys
export const banishmentKeys = {
  all: ['banishments'] as const,
  search: (filter: BanishmentFilterDto) => [...banishmentKeys.all, 'search', filter] as const,
}

export interface BanishmentFilterDto {
  firstName?: string
  lastName?: string
  birthDate?: string
  sin?: string
  sourceSystem?: string
  offset?: number
  limit?: number
}

interface BanishmentDto {
  id: string
  firstName: string
  lastName: string
  birthDate: string
  sin: string
  reason: string
  expirationDate: string | null
  sourceSystem: ESourceSystem | ''
}

export interface BanishmentCreateDto {
  firstName: string
  lastName: string
  birthDate: DateOnly
  sin: string
  reason: string
  expirationDate: DateOnly | null
}

export interface UpdateExpirationDateDto {
  newExpirationDate: DateOnly | null
}

const transformBanishmentDtoToBO = (dto: BanishmentDto): Ban => ({
  ...dto,
  birthDate: new DateOnly(dto.birthDate),
  expirationDate: dto.expirationDate ? new DateOnly(dto.expirationDate) : null,
})

const baseUrl = '/banishments'

export async function createBanishment(banishmentDto: BanishmentCreateDto): Promise<Ban> {
  const apiClient = getApiClient()
  const response = await apiClient.post<Ban>(baseUrl, banishmentDto)
  return response.data
}

export async function unbanApplicant(banishmentId: string): Promise<void> {
  const apiClient = getApiClient()
  await apiClient.delete(`${baseUrl}/${banishmentId}`)
}

export async function updateExpirationDate(
  banishmentId: string,
  newExpirationDate: UpdateExpirationDateDto,
): Promise<Ban> {
  const apiClient = getApiClient()
  const response = await apiClient.put<BanishmentDto>(`${baseUrl}/${banishmentId}/expiration`, newExpirationDate)
  return transformBanishmentDtoToBO(response.data)
}

export async function getAllBanishments(): Promise<Ban[]> {
  const apiClient = getApiClient()
  const response = await apiClient.get<Ban[]>(baseUrl)
  return response.data
}

export async function searchBanishments(filterDto: BanishmentFilterDto): Promise<Ban[]> {
  const apiClient = getApiClient()
  const response = await apiClient.post<BanishmentDto[]>(`${baseUrl}/search`, filterDto)
  return response.data.map(transformBanishmentDtoToBO)
}

export interface UpdateExpirationDateParams {
  banishmentId: string
  newExpirationDate: UpdateExpirationDateDto
}

export function useCreateBanishment(
  currentFilter?: BanishmentFilterDto,
): [MutationFunction<Ban, BanishmentCreateDto>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createBanishment,
    onSuccess: (newBan) => {
      if (currentFilter) {
        const queryKey = banishmentKeys.search(currentFilter)
        const currentData = queryClient.getQueryData<Ban[]>(queryKey)
        if (currentData) {
          queryClient.setQueryData<Ban[]>(queryKey, [...currentData, newBan])
        }
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUnbanApplicant(
  currentFilter?: BanishmentFilterDto,
): [MutationFunction<void, string>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: unbanApplicant,
    onSuccess: (_, banishmentId) => {
      if (currentFilter) {
        const queryKey = banishmentKeys.search(currentFilter)
        const currentData = queryClient.getQueryData<Ban[]>(queryKey)
        if (currentData) {
          queryClient.setQueryData<Ban[]>(
            queryKey,
            currentData.filter((ban) => ban.id !== banishmentId),
          )
        }
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateExpirationDate(
  currentFilter?: BanishmentFilterDto,
): [MutationFunction<Ban, UpdateExpirationDateParams>, boolean] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ banishmentId, newExpirationDate }: UpdateExpirationDateParams) =>
      updateExpirationDate(banishmentId, newExpirationDate),
    onSuccess: (updatedBan) => {
      if (currentFilter) {
        const queryKey = banishmentKeys.search(currentFilter)
        const currentData = queryClient.getQueryData<Ban[]>(queryKey)
        if (currentData) {
          queryClient.setQueryData<Ban[]>(
            queryKey,
            currentData.map((ban) => (ban.id === updatedBan.id ? updatedBan : ban)),
          )
        }
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useSearchBanishments(filterDto: BanishmentFilterDto): [Ban[], boolean] {
  const { data, isFetching } = useQuery<Ban[], AxiosError>({
    queryKey: banishmentKeys.search(filterDto),
    queryFn: () => searchBanishments(filterDto),
    staleTime: 0,
    gcTime: 0,
    placeholderData: [],
  })
  return [data ?? [], isFetching]
}
